import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Pantalla intermedia que procesa el `session_id` que devuelve
 * `auth.emergentagent.com` en el hash de la URL. Se monta desde
 * `App.js` cuando `location.hash.includes('session_id=')` es true, ANTES
 * de que corran las rutas normales (evita race conditions con
 * ProtectedRoute / AuthProvider).
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard sincrónico para StrictMode / doble mount.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      const hash = location.hash || '';
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('No recibimos un session_id válido. Volvé a intentar.');
        return;
      }

      try {
        const res = await axios.post(`${API_URL}/auth/google/session`, {
          session_id: sessionId,
        });
        const data = res.data || {};

        // Limpiar hash de la URL (sin recargar).
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (_) {}

        if (data.needs_linking) {
          navigate('/vincular-cuenta', {
            replace: true,
            state: {
              googleEmail: data.google_email,
              googleName: data.google_name,
              googlePicture: data.google_picture,
            },
          });
          return;
        }

        // Login directo: persistir JWT propio (misma convención que AuthContext).
        if (data.access_token && data.user) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          // Si hay un redirect pendiente (ej: viene del QR de sellos), respetarlo.
          const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
          if (redirectUrl && redirectUrl !== '/login') {
            sessionStorage.removeItem('redirectAfterLogin');
            window.location.replace(redirectUrl);
            return;
          }
          // Fuerzo un reload para que AuthContext levante el token de localStorage
          // y ProtectedRoute reconozca la sesión sin lógica adicional.
          window.location.replace('/');
          return;
        }

        setError('Respuesta inesperada del servidor. Intentá nuevamente.');
      } catch (e) {
        console.error('[AuthCallback] Error:', e);
        setError(
          e?.response?.data?.detail ||
            'No pudimos completar el inicio de sesión con Google. Intentá otra vez.'
        );
      }
    })();
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="auth-callback">
      <div className="bg-white shadow-md rounded-lg p-8 max-w-md w-full text-center">
        {!error ? (
          <>
            <Loader2 className="w-10 h-10 text-amber-600 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Iniciando sesión…</h1>
            <p className="text-sm text-gray-600">Estamos validando tu cuenta de Google.</p>
          </>
        ) : (
          <>
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Ups, hubo un problema</h1>
            <p className="text-sm text-gray-600 mb-4" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md text-sm"
              data-testid="btn-volver-login"
            >
              Volver al inicio de sesión
            </button>
          </>
        )}
      </div>
    </div>
  );
}
